import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import bandtangoLogo from './bandtango_scaled.png';
import './App.css';
import './index.css';
import { apiUrl } from './config';
import useUserStore from './UserStore';

function StripeConfirmationSection({
	invoiceId,
	onBack,
	onPaymentComplete,
}) {
	const stripe = useStripe();
	const elements = useElements();
	const [isConfirming, setIsConfirming] = useState(false);
	const [confirmError, setConfirmError] = useState('');

	const handleConfirmPayment = async (event) => {
		event.preventDefault();
		setConfirmError('');

		if (!stripe || !elements) {
			setConfirmError('Stripe is still loading. Please try again in a moment.');
			return;
		}

		setIsConfirming(true);
		try {
			const returnUrl = `${window.location.origin}/payment/success?invoice_id=${encodeURIComponent(invoiceId)}`;
			const result = await stripe.confirmPayment({
				elements,
				confirmParams: {
					return_url: returnUrl,
				},
				redirect: 'if_required',
			});

			if (result.error) {
				setConfirmError(result.error.message || 'Payment confirmation failed');
				return;
			}

			const status = result.paymentIntent?.status;
			if (status === 'succeeded') {
				onPaymentComplete('success');
				return;
			}

			if (status === 'processing') {
				onPaymentComplete('next');
				return;
			}

			onPaymentComplete('success');
		} catch (confirmError) {
			console.error('Stripe payment confirmation failed:', confirmError);
			setConfirmError(confirmError.message || 'Failed to confirm payment');
		} finally {
			setIsConfirming(false);
		}
	};

	return (
		<form className="mt-6 space-y-5 rounded-3xl border border-slate-200 bg-slate-50 p-5" onSubmit={handleConfirmPayment}>
			<div className="flex items-center justify-between gap-4">
				<div>
					<h3 className="text-lg font-semibold text-slate-900">Confirm Payment</h3>
					<p className="text-sm text-slate-500">Complete the payment details using Stripe.</p>
				</div>
				<button type="button" onClick={onBack} className="rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
					Change details
				</button>
			</div>

			<div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200">
				<PaymentElement options={{ layout: 'tabs' }} />
			</div>

			{confirmError && (
				<div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
					{confirmError}
				</div>
			)}

			<button
				type="submit"
				disabled={isConfirming || !stripe || !elements}
				className="w-full rounded-full bg-indigo-600 px-5 py-3 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
			>
				{isConfirming ? 'Processing…' : 'Pay Now'}
			</button>
		</form>
	);
}

function InvoicePayForm() {
	const navigate = useNavigate();
	const isAuthorized = useUserStore((state) => state.isAuthorized);
	const hasMenuOffset = isAuthorized;
	const { invoiceId } = useParams();
	const [isLoading, setIsLoading] = useState(true);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState('');
	const [invoice, setInvoice] = useState(null);
	const [customer, setCustomer] = useState(null);
	const [paymentConfig, setPaymentConfig] = useState(null);
	const [paymentIntent, setPaymentIntent] = useState(null);
	const [paymentMethod, setPaymentMethod] = useState('card');
	const stripePromise = useMemo(() => {
		const publishableKey = paymentConfig?.publishableKey
			|| paymentConfig?.publishable_key
			|| paymentConfig?.stripePublishableKey
			|| paymentConfig?.stripe_publishable_key
			|| paymentConfig?.stripePublicKey
			|| paymentConfig?.stripe_public_key
			|| '';
		return publishableKey ? loadStripe(publishableKey) : null;
	}, [paymentConfig]);
	const [paymentDetails, setPaymentDetails] = useState({
		payerName: '',
		email: '',
		phone: '',
		memo: '',
	});

	const formatCurrency = (value) => new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: 'USD',
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(Number(value || 0));

	const formatDate = (value) => {
		if (!value) return '—';
		try {
			return new Date(value).toLocaleDateString();
		} catch {
			return String(value);
		}
	};

	const invoiceBalance = useMemo(() => {
		const amountDue = Number(invoice?.amount_due ?? 0);
		return amountDue > 0 ? amountDue : 0;
	}, [invoice]);

	useEffect(() => {
		const loadInvoiceData = async () => {
			setIsLoading(true);
			setError('');

			try {
				const configResponse = await fetch(apiUrl('/payment/config'), {
					method: 'GET',
					headers: {
						'Content-Type': 'application/json',
					},
				});

				if (configResponse.ok) {
					const configBody = await configResponse.json();
					setPaymentConfig(configBody?.data || configBody || null);
				} else {
					setPaymentConfig(null);
				}

				const invoiceEndpoints = [
					`/invoice/one/${invoiceId}`,
					`/invoice/byid/${invoiceId}`,
					`/invoice/${invoiceId}`,
				];

				let loadedInvoice = null;
				for (const endpoint of invoiceEndpoints) {
					const response = await fetch(apiUrl(endpoint), {
						method: 'GET',
						headers: {
							'Content-Type': 'application/json',
						},
					});

					if (!response.ok) {
						if (response.status === 404) {
							continue;
						}
						throw new Error(`Failed to load invoice: ${response.status} ${response.statusText}`);
					}

					const body = await response.json();
					loadedInvoice = body?.data || body;
					if (loadedInvoice) {
						break;
					}
				}

				if (!loadedInvoice) {
					throw new Error('Invoice not found');
				}

				setInvoice(loadedInvoice);

				const customerId = loadedInvoice.customer_id;
				if (customerId) {
					const customerResponse = await fetch(apiUrl(`/customer/one?customerId=${encodeURIComponent(customerId)}`));
					if (customerResponse.ok) {
						const loadedCustomer = await customerResponse.json();
						setCustomer(loadedCustomer);
						setPaymentDetails((prev) => ({
							...prev,
							payerName: prev.payerName || loadedCustomer.billing_contact_name || loadedCustomer.contact_name || loadedCustomer.name || '',
							email: prev.email || loadedCustomer.billing_contact_email || loadedCustomer.contact_email || '',
							phone: prev.phone || loadedCustomer.billing_contact_phone || loadedCustomer.contact_phone || loadedCustomer.telephone || '',
						}));
					} else {
						setCustomer(null);
					}
				}
			} catch (loadError) {
				console.error('Error loading invoice payment details:', loadError);
				setError(loadError.message || 'Failed to load invoice');
			} finally {
				setIsLoading(false);
			}
		};

		loadInvoiceData();
	}, [invoiceId]);

	const handleInputChange = (event) => {
		const { name, value } = event.target;
		setPaymentDetails((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const handleSubmitPayment = async (event) => {
		event.preventDefault();
		setError('');

		if (!invoice) {
			setError('Invoice details are unavailable.');
			return;
		}

		if (!paymentDetails.payerName || !paymentDetails.email) {
			setError('Payer name and email are required.');
			return;
		}

		setIsSubmitting(true);
		try {
			const amountInCents = Math.max(0, Math.round(invoiceBalance * 100));
			const description = `${invoice?.name || invoice?.order_campaign || 'Invoice'}${invoice?.due_date ? ` - ${new Date(invoice.due_date).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}` : ''}`;

			const paymentPayload = {
				amount: amountInCents,
				currency: 'USD',
				customerEmail: paymentDetails.email,
				description,
				invoiceId,
				paymentMethodType: 'card',
				paymentMethodTypes: ['card'],
				automaticPaymentMethods: true,
				payerName: paymentDetails.payerName,
				payerEmail: paymentDetails.email,
				payerPhone: paymentDetails.phone,
				memo: paymentDetails.memo,
			};

			const intentResponse = await fetch(apiUrl(`/payment/create-intent/from-invoice/${encodeURIComponent(invoiceId)}`), {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Accept: 'application/json',
				},
				body: JSON.stringify({
					...paymentPayload,
					payment_config: paymentConfig,
					success_url: `${window.location.origin}/payment/success?invoice_id=${encodeURIComponent(invoiceId)}`,
					next_url: `${window.location.origin}/payment/next?invoice_id=${encodeURIComponent(invoiceId)}`,
				}),
			});

			if (!intentResponse.ok) {
				const errorText = await intentResponse.text();
				throw new Error(errorText || `Failed to create payment intent: ${intentResponse.status} ${intentResponse.statusText}`);
			}

			const intentBody = await intentResponse.json();
			const intentData = intentBody?.data || intentBody || {};
			const clientSecret = intentData?.clientSecret || intentData?.client_secret || intentData?.paymentIntent?.clientSecret || intentData?.payment_intent_client_secret || '';
			setPaymentIntent({
				...intentData,
				clientSecret,
			});

			const redirectUrl = intentData?.redirect_url || intentData?.checkout_url || intentData?.payment_url || intentData?.url;

			if (redirectUrl) {
				window.location.href = redirectUrl;
				return;
			}

			if (intentData?.success_url) {
				window.location.href = intentData.success_url;
				return;
			}

			if (intentData?.next_url) {
				window.location.href = intentData.next_url;
				return;
			}

			if (clientSecret) {
				return;
			}

			navigate(`/payment/success?invoice_id=${encodeURIComponent(invoiceId)}`);
		} catch (submitError) {
			console.error('Payment submission failed:', submitError);
			setError(submitError.message || 'Failed to submit payment');
		} finally {
			setIsSubmitting(false);
		}
	};

	const invoiceLines = Array.isArray(invoice?.entry_lines) ? invoice.entry_lines : [];
	const billingName = customer?.billing_name || customer?.name || invoice?.name || 'Customer';
	const billingEmail = customer?.billing_contact_email || customer?.contact_email || paymentDetails.email || '—';
	const billingPhone = customer?.billing_contact_phone || customer?.contact_phone || customer?.telephone || paymentDetails.phone || '—';
	const billingAddress = [
		customer?.billing_address_1 || customer?.address_1 || '',
		customer?.billing_address_2 || customer?.address_2 || '',
		[
			customer?.billing_city || customer?.city || '',
			customer?.billing_state || customer?.state || '',
			customer?.billing_zip_code || customer?.zip_code || '',
		].filter(Boolean).join(' '),
	].filter(Boolean);

	if (isLoading) {
		return (
			<div className={`${hasMenuOffset ? 'lg:pl-72' : ''} sm:ml-0 px-0 sm:px-0 min-h-screen bg-slate-50`}>
				<div className="px-4 py-10 sm:px-6 lg:px-8">
					<div className="mx-auto max-w-5xl rounded-2xl bg-white p-8 shadow-sm ring-1 ring-slate-200">
						<div className="text-center text-slate-500">Loading invoice payment details...</div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className={`${hasMenuOffset ? 'lg:pl-72' : ''} sm:ml-0 px-0 sm:px-0 min-h-screen bg-slate-50`}>
			<div className="mx-auto max-w-6xl space-y-6 px-4 py-10 sm:px-6 lg:px-8">
				<div className="overflow-hidden rounded-3xl bg-gradient-to-r from-indigo-600 via-blue-600 to-cyan-600 shadow-xl">
					{/* Mobile: stacked logo → month → amount; Desktop: logo panel left, month+amount right */}
					<div className="flex flex-col lg:flex-row">
						{/* Logo panel — full-width top bar on mobile, left sidebar on desktop */}
						<div className="flex justify-start border-b border-white/20 bg-slate-700 px-4 py-6 lg:w-56 lg:border-b-0 lg:border-r lg:flex-col lg:justify-center lg:py-10">
							<img src={bandtangoLogo} alt="Bandtango" className="h-16 w-auto object-contain lg:h-20" />
						</div>
						{/* Month + amount:
						    Mobile/Medium — two columns, date left + amount right
						    Large+        — spacer | centered date | amount right */}
						<div className="flex-1 px-6 py-8 sm:px-10">
							<div className="grid grid-cols-2 items-center gap-2 lg:grid-cols-[1fr_auto_1fr]">
								<div className="self-center text-left lg:col-end-1 lg:text-center">
									<p className="text-xs font-semibold uppercase tracking-wider text-indigo-100 sm:text-sm lg:tracking-[0.2em]">Invoice</p>
									<h1 className="mt-1 text-2xl font-bold leading-tight text-white lg:mt-2">
										{invoice?.due_date
											? new Date(invoice.due_date).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
											: 'Invoice'}
									</h1>
								</div>
								<div className="flex self-center justify-end lg:col-start-3">
									<div className="rounded-xl bg-white/15 px-4 py-3 text-right backdrop-blur sm:rounded-2xl sm:px-6 sm:py-4 lg:w-max">
										<div className="text-[10px] uppercase tracking-wide text-indigo-100">Balance Due</div>
										<div className="mt-0.5 whitespace-nowrap text-xl font-bold text-white sm:mt-1">{formatCurrency(invoiceBalance)}</div>
										<div className="mt-0.5 text-xs text-indigo-100 sm:mt-1">Due {formatDate(invoice?.due_date)}</div>
										{paymentConfig?.provider_name ? (
											<div className="mt-2 text-[10px] uppercase tracking-wide text-indigo-100">{paymentConfig.provider_name}</div>
										) : null}
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				{error && (
					<div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
						{error}
					</div>
				)}

				<div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
					<section className="rounded-3xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
						<div className="flex items-start justify-between gap-4 border-b border-slate-200 pb-5">
							<div>
								<h2 className="text-xl font-semibold text-slate-900">Invoice Summary</h2>
								<p className="mt-1 text-sm text-slate-500">Status • {invoice?.status || 'Open'}</p>
							</div>
						</div>

						<div className="mt-6 grid gap-5 sm:grid-cols-2">
							<div className="rounded-2xl bg-slate-50 p-4">
								<div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Bill To</div>
								<div className="mt-2 text-base font-semibold text-slate-900">{billingName}</div>
								<div className="mt-2 space-y-1 text-sm text-slate-600">
									{billingAddress.length > 0 ? billingAddress.map((line) => (
										<div key={line}>{line}</div>
									)) : <div>Address unavailable</div>}
									<div>{billingEmail}</div>
									<div>{billingPhone}</div>
								</div>
							</div>

							<div className="rounded-2xl bg-slate-50 p-4">
								<div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Invoice Details</div>
								<dl className="mt-3 space-y-2 text-sm text-slate-700">
									<div className="flex justify-between gap-4"><dt>Campaign</dt><dd className="font-medium text-slate-900">{invoice?.order_campaign || '—'}</dd></div>
									<div className="flex justify-between gap-4"><dt>Due Date</dt><dd className="font-medium text-slate-900">{formatDate(invoice?.due_date)}</dd></div>
									<div className="flex justify-between gap-4"><dt>Total Value</dt><dd className="font-medium text-slate-900">{formatCurrency(invoice?.total_value)}</dd></div>
									<div className="flex justify-between gap-4"><dt>Amount Due</dt><dd className="font-medium text-emerald-700">{formatCurrency(invoiceBalance)}</dd></div>
								</dl>
							</div>
						</div>

						<div className="mt-6 overflow-hidden rounded-2xl ring-1 ring-slate-200">
							<div className="bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-800">Invoice Line Items</div>
							<div className="overflow-x-auto">
								<table className="min-w-full divide-y divide-slate-200 text-sm">
									<thead className="bg-white text-left text-slate-500">
										<tr>
											<th className="px-4 py-3 font-medium">Cart</th>
											<th className="px-4 py-3 font-medium">Title</th>
											<th className="px-4 py-3 font-medium">Dates</th>
											<th className="px-4 py-3 font-medium">Impressions</th>
											<th className="px-4 py-3 text-right font-medium">Amount</th>
										</tr>
									</thead>
									<tbody className="divide-y divide-slate-100 bg-white">
										{invoiceLines.length === 0 ? (
											<tr>
												<td colSpan={5} className="px-4 py-8 text-center text-slate-500">No invoice lines available.</td>
											</tr>
										) : invoiceLines.map((line, index) => (
											<tr key={`${line.cart_number || 'line'}-${index}`}>
												<td className="px-4 py-3 font-medium text-slate-900">{line.cart_number || '—'}</td>
												<td className="px-4 py-3 text-slate-700">{line.title || line.client || 'Untitled'}</td>
												<td className="px-4 py-3 text-slate-600">{formatDate(line.start_date)} - {formatDate(line.end_date)}</td>
												<td className="px-4 py-3 text-slate-600">{line.impressions ?? '—'}</td>
												<td className="px-4 py-3 text-right font-medium text-slate-900">{formatCurrency(line.amount)}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</div>
					</section>

					<section className="rounded-3xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
						{invoiceBalance === 0 ? (
							<div className="flex flex-col items-center justify-center space-y-6 py-12">
								<div className="text-center">
									<h2 className="text-2xl font-bold text-slate-900">Thank You for your Payment!</h2>
									<p className="mt-4 text-lg text-slate-700">Thank you for supporting our local community.</p>
									<p className="mt-2 text-lg text-slate-700">You are supporting us in our mission of making music fair again.</p>
								</div>
							</div>
						) : (
							<>
								<div>
									<h2 className="text-xl font-semibold text-slate-900">Payment Details</h2>
									<p className="mt-1 text-sm text-slate-500">Stripe will present the available payment methods for this invoice.</p>
								</div>

								<form className="mt-6 space-y-5" onSubmit={handleSubmitPayment}>
									<div className="grid gap-4 sm:grid-cols-2">
										<div>
											<label htmlFor="payerName" className="block text-sm font-medium text-slate-700">Payer Name</label>
											<input id="payerName" name="payerName" value={paymentDetails.payerName} onChange={handleInputChange} className="mt-2 block w-full rounded-xl border-0 px-4 py-3 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-indigo-600" required />
										</div>
										<div>
											<label htmlFor="email" className="block text-sm font-medium text-slate-700">Email</label>
											<input id="email" type="email" name="email" value={paymentDetails.email} onChange={handleInputChange} className="mt-2 block w-full rounded-xl border-0 px-4 py-3 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-indigo-600" required />
										</div>
										<div>
											<label htmlFor="phone" className="block text-sm font-medium text-slate-700">Phone</label>
											<input id="phone" name="phone" value={paymentDetails.phone} onChange={handleInputChange} className="mt-2 block w-full rounded-xl border-0 px-4 py-3 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-indigo-600" />
										</div>
										<div>
											<label htmlFor="memo" className="block text-sm font-medium text-slate-700">Memo</label>
											<input id="memo" name="memo" value={paymentDetails.memo} onChange={handleInputChange} placeholder="Optional note" className="mt-2 block w-full rounded-xl border-0 px-4 py-3 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-indigo-600" />
										</div>
									</div>
									<div className="rounded-2xl bg-slate-900 px-5 py-4 text-white">
										<div className="flex items-center justify-between gap-4">
											<div>
												<div className="text-xs uppercase tracking-wide text-slate-300">Payment Total</div>
												<div className="mt-1 text-2xl font-bold">{formatCurrency(invoiceBalance)}</div>
											</div>
											<button type="submit" disabled={isSubmitting} className="rounded-full bg-white px-5 py-3 text-sm font-semibold text-slate-900 shadow-sm hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60">
												{isSubmitting ? 'Creating Intent…' : 'Continue to Payment'}
											</button>
										</div>
									</div>
								</form>

								{paymentIntent?.clientSecret && stripePromise ? (
									<Elements
										stripe={stripePromise}
										options={{
											clientSecret: paymentIntent.clientSecret,
											appearance: { theme: 'stripe' },
										}}
									>
										<StripeConfirmationSection
											invoiceId={invoiceId}
											onBack={() => setPaymentIntent(null)}
											onPaymentComplete={(result) => {
												if (result === 'next') {
													navigate(`/payment/next?invoice_id=${encodeURIComponent(invoiceId)}`);
													return;
												}
												navigate(`/payment/success?invoice_id=${encodeURIComponent(invoiceId)}`);
											}}
										/>
									</Elements>
								) : paymentIntent?.clientSecret && !stripePromise ? (
									<div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
										There is a problem with the payment configuration. Please contact support.
									</div>
								) : null}
							</>
						)}
					</section>
				</div>
			</div>
		</div>
	);
}

export default InvoicePayForm;
